import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Search, X, Plus } from 'lucide-react';

export const SearchableDropdown = ({
  options = [],
  placeholder = 'Select an option...',
  searchPlaceholder = 'Search...',
  value = null,
  onChange,
  onSearch,
  onAdd,
  loading = false,
  adding = false,
  disabled = false,
  clearable = true,
  noOptionsText = 'No options found',
  showAddButton = true,
  addButtonText = 'Add new option',
  className = '',
  valueClassName = '',
  slugBased = false, // If true, options will be slugified
  theme = 'light', // 'light' or 'dark'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Handle search input change
  const handleSearchChange = (e) => {
    const query =slugBased ? e.target.value.replaceAll(' ','-') : e.target.value;
    setSearchQuery(query);
    onSearch && onSearch(query);
  };

  // Handle option selection
  const handleOptionSelect = (option) => {
    onChange(option);
    setIsOpen(false);
    onSearch && onSearch('');
    setSearchQuery('');
  };

  // Handle clear selection
  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
  };

  // Handle add new option
  const handleAdd = async () => {
    if (onAdd && searchQuery.trim()) {
      try {
        const newOption = await onAdd(searchQuery.trim());
        if (newOption) {
          handleOptionSelect(newOption);
        }
      } catch (error) {
        console.error('Error adding new option:', error);
      }
    }
  };

  // Toggle dropdown
  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchQuery('');
      }
    }
  };

  // Filter options based on search query
  const filteredOptions = options.filter(option => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return option.label.toLowerCase().includes(query) || 
           (option.value && option.value.toLowerCase().includes(query));
  });

  // Check if we should show add button - Fixed condition
  const shouldShowAddButton = showAddButton && 
    onAdd && 
    searchQuery.trim() && 
    !loading;

  
  return (
    <div className={`relative w-full ${className}`} ref={dropdownRef}>
      
      <div className={`flex items-center cursor-pointer  duration-300 p-1 rounded-lg gap-1 ${valueClassName}`} onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        toggleDropdown()
      }}>
        <h3 className='capitalize w-full'>{value?.label || placeholder}</h3>
        {clearable && value && (
          <button
            onClick={handleClear}
            className={`ml-auto p-1 rounded-full focus:outline-none ${
              theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-200'
            }`}
            type="button"
          >
            <X size={14} className={`${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
            }`} />
          </button>
        )}
        <ChevronDown 
          size={16} 
          className={`transform transition-transform duration-200 ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
          } ${isOpen ? 'rotate-180' : ''}`} 
        />
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className={`absolute min-w-[240px] z-[9999] w-full mt-1 border rounded-md shadow-lg ${
          theme === 'dark' 
            ? 'bg-gray-800 border-gray-600' 
            : 'bg-white border-gray-300'
        }`} style={{ minHeight: '50px', backgroundColor: theme === 'dark' ? '#1f2937' : 'white' }}>
          {/* Search Input */}
          <div className={`p-2 border-b ${
            theme === 'dark' ? 'border-gray-600' : 'border-gray-200'
          }`}>
            <div className="relative">
              <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
              }`} />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder={searchPlaceholder}
                className={`w-full pl-9 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-white placeholder:text-gray-400'
                    : 'border-gray-300'
                }`}
              />
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-60 overflow-auto">
            {loading ? (
              <div className={`px-3 py-4 text-center ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                  <span className="ml-2">Loading...</span>
                </div>
              </div>
            ) : filteredOptions.length === 0 ? (
              <div className="px-3 py-2">
                <div className={`text-center py-2 ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  {searchQuery.trim() ? `No results found for "${searchQuery}"` : noOptionsText}
                </div>
                {shouldShowAddButton && (
                  <button
                    type="button"
                    onClick={handleAdd}
                    disabled={adding}
                    className="w-full mt-2 px-3 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white rounded-md transition-colors duration-200 flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    {adding ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Adding...</span>
                      </>
                    ) : (
                      <>
                        <Plus size={16} />
                        <span>{addButtonText}: "{searchQuery}"</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            ) : (
              <>
                {filteredOptions.map((option) => {
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => handleOptionSelect(option)}
                      className={`
                        w-full text-left px-3 py-2 focus:outline-none transition-colors duration-150
                        ${theme === 'dark' 
                          ? `hover:bg-gray-700 focus:bg-gray-700 ${
                              value?.id === option.id ? 'bg-blue-600 text-white' : 'text-gray-300'
                            }`
                          : `hover:bg-gray-100 focus:bg-gray-100 ${
                              value?.id === option.id ? 'bg-blue-50 text-blue-600' : 'text-gray-900'
                            }`
                        }
                      `}
                    >
                      {option.label}
                    </button>
                  )
                })}
                {/* Add button at bottom when options exist but user is searching */}
                {shouldShowAddButton && searchQuery.trim() && (
                  <div className={`border-t p-2 ${
                    theme === 'dark' ? 'border-gray-600' : 'border-gray-200'
                  }`}>
                    <button
                      type="button"
                      onClick={handleAdd}
                      disabled={adding}
                      className="w-full px-3 py-2 bg-green-500 hover:bg-green-600 disabled:bg-green-300 text-white rounded-md transition-colors duration-200 flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                    >
                      {adding ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>Adding...</span>
                        </>
                      ) : (
                        <>
                          <Plus size={16} />
                          <span>{addButtonText}: "{searchQuery}"</span>
                        </>
                      )}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
